import React, { useEffect, useRef, useState } from 'react';
import { X, PartyPopper } from 'lucide-react';
import {
  KUDOS_DEFAULT_MESSAGE,
  KUDOS_MAX_MESSAGE_LENGTH,
  KUDOS_MAX_POINTS,
  firstNameLastInitial,
  type KudosSource,
  type SendKudosResponse,
} from '@crewcast/shared';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePointsEnabled } from '../hooks/usePointsEnabled';
import { useBackdropDismiss } from '../hooks/useBackdropDismiss';
import '../styles/dashboard.css'; // moment-creator-* field styles

const SENT_CLOSE_DELAY_MS = 1500;

interface Recipient {
  userId: string;
  name: string;
}

interface Props {
  /** Who it's for; leave out to let the admin pick an employee (Employees page). */
  employee?: Recipient | null;
  /** Where it was sent from, kept on the kudos audit record. */
  source: KudosSource;
  onClose: () => void;
  /** Gets the recipient too, for callers that let the admin pick one. */
  onSent?: (result: SendKudosResponse, recipient: Recipient) => void;
}

interface EmployeeOption extends Recipient {
  fullName: string;
}

/** Every active employee in the company, following the list's page cursor. */
async function fetchActiveEmployees(token: string): Promise<EmployeeOption[]> {
  const options: EmployeeOption[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < 20; page++) {
    const params = new URLSearchParams({ limit: '100' });
    if (cursor) params.set('cursor', cursor);
    const res = await fetch(`${authConfig.apiUrl}/api/admin/employees?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Could not load employees');
    const data = await res.json();
    for (const e of data.employees || []) {
      if (e.status !== 'active') continue;
      options.push({
        userId: e.userId,
        name: firstNameLastInitial(e),
        fullName: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email,
      });
    }
    cursor = data.nextCursor;
    if (!cursor) break;
  }
  return options.sort((a, b) => a.fullName.localeCompare(b.fullName));
}

/**
 * Send kudos: write the note (and bonus points when points are on), then a
 * confirm step that spells out the points before anything is sent.
 * Web counterpart of mobile's KudosSheet; same API.
 */
export default function KudosModal({ employee, source, onClose, onSent }: Props) {
  const { token } = useAuth();
  const pointsEnabled = usePointsEnabled();
  const picking = !employee;
  const [recipient, setRecipient] = useState<Recipient | null>(employee ?? null);
  const [options, setOptions] = useState<EmployeeOption[] | null>(null);

  useEffect(() => {
    if (!picking || !token) return;
    fetchActiveEmployees(token)
      .then(setOptions)
      .catch(() => { setOptions([]); setError('Could not load employees. Close and try again.'); });
  }, [picking, token]);
  const [message, setMessage] = useState(KUDOS_DEFAULT_MESSAGE);
  const [points, setPoints] = useState('');
  const [step, setStep] = useState<'compose' | 'confirm' | 'sent'>('compose');
  const [sending, setSending] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const [error, setError] = useState('');

  const pointsNum = pointsEnabled && points ? parseInt(points, 10) : 0;
  const locked = sending;
  const backdrop = useBackdropDismiss(onClose, locked);

  const review = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!recipient) return setError('Choose an employee.');
    if (!message.trim()) return setError('Add a short message.');
    if (pointsNum > KUDOS_MAX_POINTS) {
      return setError(`Bonus points can be at most ${KUDOS_MAX_POINTS.toLocaleString()}.`);
    }
    setStep('confirm');
  };

  const send = async () => {
    if (!recipient) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${authConfig.apiUrl}/api/admin/employees/${recipient.userId}/kudos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim(), points: pointsNum, source }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not send kudos');
      setStep('sent');
      onSent?.(data, recipient);
      // Show "Kudos sent!" briefly, then close on its own (Done closes it sooner)
      closeTimer.current = setTimeout(onClose, SENT_CLOSE_DELAY_MS);
    } catch (err: any) {
      setError(err.message || 'Could not send kudos');
      setStep('compose');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="crewcast-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kudos-title"
      {...backdrop}
      onKeyDown={(e) => { if (e.key === 'Escape' && !locked) onClose(); }}
      tabIndex={-1}
    >
      <div className="crewcast-modal-card" style={{ maxWidth: '480px' }}>
        <div className="crewcast-modal-header centered">
          <h2 id="kudos-title" className="crewcast-modal-title kudos-title">
            <PartyPopper size={22} color="#ffffff" aria-hidden="true" />
            {step === 'sent' ? 'Kudos sent!' : recipient && !picking ? `Send kudos to ${recipient.name}` : 'Send kudos'}
          </h2>
          <button type="button" className="crewcast-modal-close" onClick={onClose} disabled={locked} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {step === 'compose' && (
          <form onSubmit={review}>
            <div className="crewcast-modal-body">
              {picking && (
                <div className="moment-creator-field">
                  <label className="moment-creator-label" htmlFor="kudos-employee">Employee</label>
                  <select
                    id="kudos-employee"
                    className="moment-creator-input crewcast-select"
                    value={recipient?.userId ?? ''}
                    disabled={!options}
                    onChange={(e) => {
                      setRecipient(options?.find((o) => o.userId === e.target.value) ?? null);
                      setError('');
                    }}
                  >
                    <option value="">{options ? 'Choose an employee…' : 'Loading employees…'}</option>
                    {options?.map((o) => (
                      <option key={o.userId} value={o.userId}>{o.fullName}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="moment-creator-field">
                <label className="moment-creator-label" htmlFor="kudos-message">Message</label>
                <textarea
                  id="kudos-message"
                  className="moment-creator-input"
                  rows={4}
                  value={message}
                  maxLength={KUDOS_MAX_MESSAGE_LENGTH}
                  onChange={(e) => { setMessage(e.target.value); setError(''); }}
                />
                <div className="kudos-counter">{message.length}/{KUDOS_MAX_MESSAGE_LENGTH}</div>
              </div>
              {pointsEnabled && (
                <div className="moment-creator-field">
                  <label className="moment-creator-label" htmlFor="kudos-points">
                    Bonus points <span className="moment-creator-optional">(optional)</span>
                  </label>
                  <input
                    id="kudos-points"
                    type="text"
                    inputMode="numeric"
                    className="moment-creator-input"
                    placeholder="0"
                    value={points}
                    onChange={(e) => { setPoints(e.target.value.replace(/\D/g, '')); setError(''); }}
                  />
                </div>
              )}
              {error && <div className="crewcast-modal-error">{error}</div>}
            </div>
            <div className="crewcast-modal-actions">
              <button type="button" className="crewcast-modal-btn secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="crewcast-modal-btn primary">Review</button>
            </div>
          </form>
        )}

        {step === 'confirm' && (
          <>
            <div className="crewcast-modal-body">
              <p className="kudos-confirm">
                {recipient?.name} will get a push and an inbox message with your note
                {pointsNum > 0
                  ? <>, plus <strong>{pointsNum.toLocaleString()} bonus points</strong>.</>
                  : '.'}
              </p>
              <blockquote className="kudos-quote">{message.trim()}</blockquote>
              {error && <div className="crewcast-modal-error">{error}</div>}
            </div>
            <div className="crewcast-modal-actions">
              <button type="button" className="crewcast-modal-btn secondary" onClick={() => setStep('compose')} disabled={sending}>
                Back
              </button>
              <button type="button" className="crewcast-modal-btn primary" onClick={send} disabled={sending}>
                {sending ? 'Sending…' : pointsNum > 0 ? `Send with ${pointsNum.toLocaleString()} points` : 'Send kudos'}
              </button>
            </div>
          </>
        )}

        {step === 'sent' && (
          <>
            <div className="crewcast-modal-body">
              <p className="kudos-confirm">
                {recipient?.name} has been notified
                {pointsNum > 0 ? ` and received ${pointsNum.toLocaleString()} bonus points` : ''}.
              </p>
            </div>
            <div className="crewcast-modal-actions">
              <button type="button" className="crewcast-modal-btn primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
